import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, ShieldCheck, Brain, ToggleLeft, ToggleRight } from "lucide-react";

const TYPES = [
  { v: "user", l: "User ID" },
  { v: "organization", l: "Organization ID" },
  { v: "institution", l: "Institution ID" },
  { v: "domain", l: "Email domain" },
];

export default function TrustSafetySettings() {
  const [whitelist, setWhitelist] = useState([]);
  const [investigations, setInvestigations] = useState([]);
  const [type, setType] = useState("organization");
  const [targetId, setTargetId] = useState("");
  const [reason, setReason] = useState("");
  const [llm, setLlm] = useState(null);

  const load = async () => {
    try {
      const wl = await api.get("/admin/trust-safety/whitelist");
      setWhitelist(wl.data.rows);
      const inv = await api.get("/admin/trust-safety/investigations", { params: { limit: 50 } });
      setInvestigations(inv.data.rows);
      const ls = await api.get("/admin/trust-safety/llm-status");
      setLlm(ls.data);
    } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!targetId) { toast.error("Target ID is required"); return; }
    try {
      await api.post("/admin/trust-safety/whitelist", { target_type: type, target_id: targetId, reason });
      toast.success("Added to whitelist");
      setTargetId(""); setReason("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const remove = async (row) => {
    if (!confirm("Remove this whitelist entry?")) return;
    try {
      await api.delete("/admin/trust-safety/whitelist", { data: { target_type: row.target_type, target_id: row.target_id } });
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const toggleLLM = async () => {
    try {
      await api.post("/admin/trust-safety/llm-toggle", { enabled: !llm.enabled });
      toast.success(`LLM ${!llm.enabled ? "enabled" : "disabled"}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <AdminLayout subtitle="Trust & Safety" title="Settings & Whitelist">
      {/* LLM Toggle */}
      {llm && (
        <Card className="rounded-sm border-white/10 bg-white/[0.02] mb-6" data-testid="ts-llm-card">
          <CardContent className="p-6 flex flex-wrap items-center gap-6">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-ec-emerald" />
                <h3 className="font-display text-xl font-bold text-white">AI Investigation Summary</h3>
                <Badge className={`rounded-sm border ${llm.enabled ? "bg-ec-emerald/10 text-ec-emerald border-ec-emerald/40" : "bg-white/5 text-zinc-500 border-white/10"}`} data-testid="ts-llm-status">
                  {llm.enabled ? "Enabled" : "Disabled"}
                </Badge>
              </div>
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed">
                Rule-based summaries are <span className="text-white font-semibold">always on</span> and shown on every alert. When LLM is enabled, alert summaries are enhanced via Emergent LLM key (Claude Sonnet 4.5) for more nuanced phrasing and recommendations.
              </p>
              <p className="text-[11px] text-zinc-500 mt-2 font-mono-plex">Model · {llm.model}</p>
            </div>
            <Button onClick={toggleLLM} variant="outline" className={`rounded-sm gap-2 ${llm.enabled ? "border-ec-error/40 text-ec-error hover:bg-ec-error/10" : "border-ec-emerald/40 text-ec-emerald hover:bg-ec-emerald/10"}`} data-testid="ts-llm-toggle">
              {llm.enabled ? <><ToggleRight className="w-4 h-4" />Turn off LLM</> : <><ToggleLeft className="w-4 h-4" />Turn on LLM</>}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Whitelist */}
      <Card className="rounded-sm border-white/10 bg-white/[0.02] mb-6">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="w-5 h-5 text-ec-emerald" />
            <h3 className="font-display text-xl font-bold text-white">Whitelist</h3>
            <Badge className="bg-white/10 border-white/10 rounded-sm">{whitelist.length}</Badge>
          </div>
          <p className="text-sm text-zinc-400 mb-4 leading-relaxed">
            Whitelisted accounts and domains are excluded from fraud scoring — useful for verified universities, government bodies, and trusted enterprise partners.
          </p>
          <div className="grid sm:grid-cols-[160px_1fr_2fr_auto] gap-2 mb-4" data-testid="ts-whitelist-form">
            <select value={type} onChange={(e) => setType(e.target.value)} className="rounded-sm h-9 px-3 bg-white/5 border border-white/10 text-white text-sm" data-testid="ts-whitelist-type">
              {TYPES.map((t) => <option key={t.v} value={t.v} className="bg-ec-ink">{t.l}</option>)}
            </select>
            <Input value={targetId} onChange={(e) => setTargetId(e.target.value)} placeholder={type === "domain" ? "example.com" : "ID"} className="rounded-sm h-9 bg-white/5 border-white/10 text-white" data-testid="ts-whitelist-target" />
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (e.g. Verified university, Govt body)" className="rounded-sm h-9 bg-white/5 border-white/10 text-white" data-testid="ts-whitelist-reason" />
            <Button onClick={add} className="rounded-sm h-9 bg-ec-emerald hover:bg-ec-emerald/90 text-ec-ink gap-1" data-testid="ts-whitelist-add"><Plus className="w-3 h-3" />Add</Button>
          </div>
          {whitelist.length === 0 ? (
            <div className="py-8 text-center text-zinc-500 text-sm" data-testid="ts-whitelist-empty">No entries yet.</div>
          ) : null}
          <ul className="divide-y divide-white/5" data-testid="ts-whitelist-list">
            {whitelist.map((w) => (
                <li key={`${w.target_type}-${w.target_id}`} className="flex items-center gap-3 py-2.5" data-testid={`ts-whitelist-row-${w.target_id}`}>
                  <Badge className="bg-white/10 border-white/10 rounded-sm w-28 justify-center">{w.target_type}</Badge>
                  <span className="font-mono-plex text-sm flex-1 truncate">{w.target_id}</span>
                  <span className="text-xs text-zinc-500 flex-1 truncate">{w.reason || "—"}</span>
                  <Button size="sm" variant="outline" className="rounded-sm h-7 border-ec-error/30 text-ec-error hover:bg-ec-error/10 gap-1" onClick={() => remove(w)} data-testid={`ts-whitelist-remove-${w.target_id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </li>
              ))}
          </ul>
        </CardContent>
      </Card>

      {/* Investigation log */}
      <Card className="rounded-sm border-white/10 bg-white/[0.02]">
        <CardContent className="p-6">
          <h3 className="font-display text-xl font-bold mb-3 text-white">Investigation log</h3>
          <p className="text-sm text-zinc-400 mb-4">All trust & safety enforcement actions, with admin identity and reason.</p>
          {investigations.length === 0 ? (
            <div className="py-8 text-center text-zinc-500 text-sm" data-testid="ts-invest-empty">No investigations yet.</div>
          ) : (
            <ul className="divide-y divide-white/5" data-testid="ts-invest-list">
              {investigations.map((i) => (
                <li key={i.id} className="py-3 flex items-start gap-3 text-sm" data-testid={`ts-invest-${i.id}`}>
                  <div className="font-mono-plex text-[10px] text-zinc-500 w-36 flex-shrink-0">{i.created_at?.slice(0, 16)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className="bg-white/10 border-white/10 rounded-sm font-mono-plex text-[10px]">{i.action}</Badge>
                      <span className="text-xs text-zinc-400">{i.detector_key}</span>
                      <span className="text-[10px] font-mono-plex text-zinc-600">by {i.admin_email}</span>
                    </div>
                    {i.note && <div className="text-xs text-zinc-300 mt-1">{i.note}</div>}
                    {i.reason && <div className="text-[10px] text-zinc-500 mt-1">on alert: {i.reason}</div>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
